import {
  ABSENCE_TYPE_OPTIONS,
  absenceMarkerClassName,
  formatAbsenceMarkerLabel,
  getAbsenceTypeLabel,
  type AbsenceDayMarker,
  type AbsenceType,
} from "@/lib/employeeAbsences";
import { HeroInlineLogo } from "@/components/dataTable";

function legendSample(type: AbsenceType): AbsenceDayMarker {
  return {
    date: "",
    absence_type: type,
    status: "approved",
    day_fraction: 1,
    half_day_part: null,
  };
}

export function AbsenceCalendarLegend({ showHeroLegend = false }: { showHeroLegend?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-label-secondary">
      <span className="font-medium text-label">Legende</span>
      {ABSENCE_TYPE_OPTIONS.map((opt) => {
        const sample = legendSample(opt.value);
        return (
          <span
            key={opt.value}
            title={getAbsenceTypeLabel(opt.value)}
            className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${absenceMarkerClassName(sample)}`}
          >
            {formatAbsenceMarkerLabel(sample)}
          </span>
        );
      })}
      <span
        className={`inline-flex items-center rounded-md border border-dashed px-1.5 py-0.5 text-[10px] font-semibold ${absenceMarkerClassName({ ...legendSample("urlaub"), status: "pending" })}`}
      >
        Ausstehend
      </span>
      <span className="inline-flex items-center gap-1 border-l border-separator pl-3 text-label-secondary">
        <span className="inline-flex rounded-md bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-900 dark:text-amber-200">FT</span>
        Feiertag
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-flex rounded-md bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-bold text-indigo-800 dark:text-indigo-200">B</span>
        Brückentag
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="inline-flex rounded-md bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-bold text-sky-900 dark:text-sky-200">BF</span>
        Betriebsferien
      </span>
      {showHeroLegend && (
        <span className="inline-flex items-center gap-1 border-l border-separator pl-3 text-label-secondary">
          <span className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-violet-500/40 bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold text-violet-800 dark:text-violet-200">
            <HeroInlineLogo />
            U
          </span>
          Hero-Abwesenheit
        </span>
      )}
      <span className="inline-flex items-center gap-1 border-l border-separator pl-3 text-label-secondary">
        <span
          className="inline-block h-3 w-3 rounded-sm border border-separator bg-fill-secondary"
          aria-hidden
        />
        Wochenende
      </span>
    </div>
  );
}

export function AbsenceDayBadges({ markers }: { markers: AbsenceDayMarker[] }) {
  if (!markers.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-0.5">
      {markers.map((marker, idx) => (
        <span
          key={`${marker.date}-${marker.absence_type}-${idx}`}
          className={`rounded px-1 py-0.5 text-[10px] font-semibold leading-tight ${absenceMarkerClassName(marker)}`}
          title={getAbsenceTypeLabel(marker.absence_type)}
        >
          {formatAbsenceMarkerLabel(marker)}
        </span>
      ))}
    </div>
  );
}

export function HeroAbsenceDayBadges({ markers }: { markers: AbsenceDayMarker[] }) {
  if (!markers.length) return null;
  return (
    <div className="mt-0.5 flex flex-wrap gap-0.5">
      {markers.map((marker, idx) => (
        <span
          key={`hero-${marker.date}-${marker.absence_type}-${idx}`}
          className={`inline-flex items-center gap-0.5 rounded border border-dashed border-violet-500/40 px-1 py-0.5 text-[10px] font-semibold leading-tight ${absenceMarkerClassName(marker)}`}
          title={`Hero: ${getAbsenceTypeLabel(marker.absence_type)}`}
        >
          <HeroInlineLogo />
          {formatAbsenceMarkerLabel(marker)}
        </span>
      ))}
    </div>
  );
}
